import { rawApi as api } from '../utils/apiWithLogging';
import {
  SupplierRepair,
  SupplierRepairCreate,
  SupplierRepairUpdate,
  SupplierRepairStatusChange,
  SupplierRepairListResponse,
  SupplierRepairDashboardStats,
  SupplierRepairStatusHistory,
  GetSupplierRepairsParams
} from '../types/supplier-repair';

const API_BASE = '/supplier-repairs';

/**
 * Service สำหรับจัดการข้อมูลการส่งซ่อมผ่านซัพพลายเออร์
 */
class SupplierRepairService {
  /**
   * ดึงรายการส่งซ่อมทั้งหมด
   */
  async getSupplierRepairs(params?: GetSupplierRepairsParams): Promise<SupplierRepairListResponse> {
    const response = await api.get<SupplierRepairListResponse>(`${API_BASE}/`, { params });
    return response.data;
  }

  /**
   * ดึงข้อมูลการส่งซ่อมตาม ID
   */
  async getSupplierRepairById(id: number): Promise<SupplierRepair> {
    const response = await api.get<SupplierRepair>(`${API_BASE}/${id}`);
    return response.data;
  }

  /**
   * สร้างรายการส่งซ่อมใหม่
   */
  async createSupplierRepair(data: SupplierRepairCreate): Promise<SupplierRepair> {
    const response = await api.post<SupplierRepair>(`${API_BASE}/`, data);
    return response.data;
  }

  /**
   * แก้ไขข้อมูลการส่งซ่อม
   */
  async updateSupplierRepair(id: number, data: SupplierRepairUpdate): Promise<SupplierRepair> {
    const response = await api.put<SupplierRepair>(`${API_BASE}/${id}`, data);
    return response.data;
  }

  /**
   * ลบรายการส่งซ่อม
   */
  async deleteSupplierRepair(id: number): Promise<void> {
    await api.delete(`${API_BASE}/${id}`);
  }

  /**
   * เปลี่ยนสถานะการส่งซ่อม
   */
  async changeStatus(id: number, data: SupplierRepairStatusChange): Promise<SupplierRepair> {
    const response = await api.post<SupplierRepair>(`${API_BASE}/${id}/status`, data);
    return response.data;
  }

  /**
   * ดึงประวัติการเปลี่ยนสถานะ
   */
  async getStatusHistory(id: number): Promise<SupplierRepairStatusHistory[]> {
    const response = await api.get<SupplierRepairStatusHistory[]>(`${API_BASE}/${id}/history`);
    return response.data;
  }

  /**
   * ดึงสถิติภาพรวมสำหรับ Dashboard
   */
  async getDashboardStats(): Promise<SupplierRepairDashboardStats> {
    const response = await api.get<SupplierRepairDashboardStats>(`${API_BASE}/dashboard`);
    return response.data;
  }

  // C3 (MANUAL_AUDIT_SYSTEM_FINDINGS): ลบ getOverdueRepairs() ออกแล้ว — 0 caller
  // หน้าจอใช้ตัวกรอง `is_overdue` ของ getSupplierRepairs() แทนอยู่แล้ว (ปุ่ม
  // "เฉพาะที่เกินกำหนด") จึงไม่มีที่ทางให้ wrapper ตัวนี้ · backend ยังมี
  // GET /supplier-repairs/overdue อยู่ถ้าวันหน้าต้องใช้

  /**
   * ส่งออกข้อมูลเป็น Excel
   */
  async exportToExcel(params?: GetSupplierRepairsParams): Promise<Blob> {
    const response = await api.get(`${API_BASE}/export/excel`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * ดึงรายการส่งซ่อมตามอุปกรณ์
   */
  async getRepairsByDevice(deviceId: number): Promise<SupplierRepair[]> {
    const response = await api.get<SupplierRepairListResponse>(`${API_BASE}/`, {
      params: { device_id: deviceId }
    });
    return response.data.items;
  }

  /**
   * ดึงรายการส่งซ่อมตามซัพพลายเออร์
   */
  async getRepairsBySupplier(supplierId: number): Promise<SupplierRepair[]> {
    const response = await api.get<SupplierRepairListResponse>(`${API_BASE}/`, {
      params: { supplier_id: supplierId }
    });
    return response.data.items;
  }
}

export default new SupplierRepairService();
